import { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Copy, Check, Link2 } from 'lucide-react';
import { api } from '../lib/api';
import type { Node } from '@hongweibing-netdisk/shared';
import toast from 'react-hot-toast';
import { backdrop, scaleIn } from '../lib/motion';

export function ShareDialog({ node, onClose }: { node: Node; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [days, setDays] = useState('7');
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const createShare = async () => {
    setLoading(true);
    try {
      const expiresAt = days === '' ? undefined : Date.now() + parseInt(days, 10) * 86400000;
      const data = await api.shares.create({
        nodeId: node.id,
        password: password || undefined,
        permission: 'read',
        expiresAt,
      });
      setShareUrl(data.shareUrl);
      toast.success('分享链接已创建');
    } catch (e: any) {
      toast.error(e.message || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <motion.div variants={backdrop} initial="hidden" animate="show" exit="exit" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div variants={scaleIn} className="bg-card/95 backdrop-blur-xl border border-border-soft rounded-2xl p-5 w-full max-w-md soft-shadow" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2 mb-4">
          <h3 className="text-base sm:text-lg font-semibold text-text flex items-center gap-2 min-w-0">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 flex-shrink-0"><Link2 className="w-4 h-4 text-primary" /></span>
            <span className="truncate">分享「{node.name}」</span>
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-card text-text-muted flex-shrink-0 transition-colors btn-press"><X className="w-5 h-5" /></button>
        </div>

        {!shareUrl ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-text-muted mb-1.5">访问密码（可选）</label>
              <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="不填则无需密码"
                className="w-full px-3 py-2.5 bg-bg/60 border border-border rounded-xl text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all" />
            </div>
            <div>
              <label className="block text-sm text-text-muted mb-1.5">有效期</label>
              <select value={days} onChange={(e) => setDays(e.target.value)}
                className="w-full px-3 py-2.5 bg-bg/60 border border-border rounded-xl text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all">
                <option value="1">1 天</option>
                <option value="7">7 天</option>
                <option value="30">30 天</option>
                <option value="365">1 年</option>
                <option value="">永久</option>
              </select>
            </div>
            <button onClick={createShare} disabled={loading}
              className="w-full py-2.5 brand-gradient hover:brightness-110 disabled:opacity-50 text-white rounded-xl font-medium btn-press soft-shadow transition-all">
              {loading ? '创建中...' : '创建分享链接'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input readOnly value={shareUrl}
                className="flex-1 px-3 py-2.5 bg-bg/60 border border-border rounded-xl text-text text-sm focus:outline-none" />
              <button onClick={copy} className="p-2.5 bg-primary hover:bg-primary-hover text-white rounded-xl btn-press transition-colors" aria-label="复制链接">
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            {password && <p className="text-xs text-text-muted">分享密码：<span className="text-primary font-mono">{password}</span></p>}
            <button onClick={() => setShareUrl('')} className="w-full py-2.5 bg-card hover:bg-card-hover text-text rounded-xl text-sm transition-colors btn-press">重新创建</button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
